import { Heading, Stack } from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { Control, FieldValues, UseFormWatch } from "react-hook-form";

import {
  borderRadius,
  borderStyle,
  conditionalFieldStyles,
} from "../../../../utils/styles/styles";

import InputFieldComponent from "../../../../components/molecule/InputField/InputFieldComponent";
import FormSelect from "../../../../components/molecule/Select/FormSelect";

import { getIndicatorType } from "../../../../services/Indicator/getIndicatorType";
import { getCollectionFrequency } from "../../../../services/Indicator/getCollectionFrequency";
import { getSubSystem } from "../../../../services/Indicator/getSubSystem";
import { getIndicatorMetric } from "../../../../services/Indicator/getIndicatorMetric";
import { getIndicator } from "../../../../services/Indicator/getIndicator";

import api from "../../../../utils/api";
import { Auxiliares } from "../../../../types/TabelasAuxiliares/Auxiliares";

interface InformationDataFormProps {
  errors: any;
  control: Control<any>;
  isDisabled?: boolean;
  watch: UseFormWatch<FieldValues>;
  selectedIndicatorMetric: Auxiliares;
  setError: (name: any, error: any) => void;
  clearErrors: (name?: any) => void;
  setHasDuplicateNameError: (hasError: boolean) => void; // ✅ NOVO
  currentId?: number; // ✅ NOVO
}

const InformationDataForm: FC<InformationDataFormProps> = ({
  errors,
  control,
  isDisabled,
  watch,
  selectedIndicatorMetric,
  setError,
  clearErrors,
  setHasDuplicateNameError,
  currentId,
}) => {
  // Estado para validação de nome duplicado
  const [indicators, setIndicators] = useState<any[]>([]);
  const [nameError, setNameError] = useState<string | null>(null);

  // Carrega lista de indicadores ao montar
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        // Tenta usar o helper getIndicator (que retorna .content)
        const result = await getIndicator(undefined, undefined, undefined);
        const list = result?.content ?? [];
        if (mounted) setIndicators(list);
      } catch (ex) {
        // Fallback: tenta endpoint direto se o helper falhar
        try {
          const r = await api.get("/api/Indicator");
          const list = r.data?.data ?? r.data ?? [];
          if (mounted) setIndicators(list);
        } catch (err) {
          console.error("Erro ao carregar indicadores para validação:", err);
        }
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Valida duplicação do nome do indicador
  useEffect(() => {
    const validateDuplicateName = async () => {
      const nameValue = watch("name");

      if (!nameValue || typeof nameValue !== "string") {
        clearErrors("name");
        setHasDuplicateNameError(false);
        return;
      }

      const normalized = nameValue.trim().toLowerCase();

      const exists = indicators.some(
        (i) =>
          i?.name &&
          typeof i.name === "string" &&
          i.name.trim().toLowerCase() === normalized &&
          i.id !== currentId, // ✅ aqui substituímos o watch("id")
      );

      if (exists) {
        const message = "Já existe um indicador com esse nome.";
        setError("name", { type: "manual", message });
        setHasDuplicateNameError(true);
        setNameError(message);
      } else {
        clearErrors("name");
        setHasDuplicateNameError(false);
        setNameError(null);
      }
    };

    validateDuplicateName();
  }, [watch("name"), indicators, currentId]);
  return (
    <Stack
      spacing={4}
      border={borderStyle}
      borderRadius={borderRadius}
      background="gray.50"
      p={6}
    >
      <Heading as="h3" fontSize="2xl">
        Informações do indicador
      </Heading>
      <InputFieldComponent
        label="Nome"
        name="name"
        type="text"
        register={control.register}
        errors={errors}
        isRequired
        isDisabled={isDisabled}
        hasErrorBorder={!!nameError} // ✅ borda vermelha se houver duplicação
      />
      {/* Mensagem de erro já é tratada internamente no InputFieldComponent.
   Apenas mantemos a lógica de validação do nome duplicado */}
      {nameError && !errors?.name?.message && (
        <div style={{ color: "#E53E3E", marginTop: -6, fontSize: 14 }}>
          {nameError}
        </div>
      )}
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="indicatorTypeOption"
        fieldLabel="Tipo de Indicador"
        fetchOptions={getIndicatorType}
        isDisabled={isDisabled}
      />
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="subSystemOption"
        fieldLabel="Sub-Sistema"
        fetchOptions={getSubSystem}
        isDisabled={isDisabled}
      />
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="frequencyOption"
        fieldLabel="Frequência de Recolha"
        fetchOptions={getCollectionFrequency}
        isDisabled={isDisabled}
      />
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="indicatorMetricOption"
        fieldLabel="Métrica"
        fetchOptions={getIndicatorMetric}
        isDisabled={isDisabled}
      />
      {selectedIndicatorMetric?.id === 4 && (
        <InputFieldComponent
          label="Outra"
          name="outra"
          type="text"
          register={control.register}
          errors={errors}
          isRequired={selectedIndicatorMetric?.id === 4}
          isDisabled={isDisabled}
          additionalStyle={conditionalFieldStyles}
        />
      )}
    </Stack>
  );
};

export default InformationDataForm;
